/* MNRL — global.js
   Vanilla custom elements. Progressive enhancement over working HTML forms.
   No dependencies. Everything degrades to native behavior without JS. */

(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var pattern = /\{\{\s*(\w+)\s*\}\}/;
    format = format || (window.MNRL && window.MNRL.moneyFormat) || '${{amount}}';
    var match = format.match(pattern);
    if (!match) return '$' + value;
    var key = match[1];
    var formatted = value;
    if (key === 'amount_no_decimals') {
      formatted = Math.round(cents / 100).toString();
    } else if (key === 'amount_with_comma_separator') {
      formatted = value.replace('.', ',');
    }
    formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(pattern, formatted);
  }

  function announce(message) {
    var region = document.getElementById('live-region');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  function trapFocus(container, event) {
    var focusable = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------------ *
   * Drawer base: overlay + sliding panel, focus trap, Escape, restore. *
   * ------------------------------------------------------------------ */
  var DrawerBase = function (Base) {
    return class extends Base {
      connectedCallback() {
        this.panel = this.querySelector('.drawer__panel');
        this.overlay = this.querySelector('.drawer__overlay');
        this.boundKeydown = this.onKeydown.bind(this);
        if (this.overlay) {
          this.overlay.addEventListener('click', this.close.bind(this));
        }
        this.querySelectorAll('[data-drawer-close]').forEach(
          function (button) {
            button.addEventListener('click', this.close.bind(this));
          }.bind(this)
        );
      }

      open(trigger) {
        this.trigger = trigger || document.activeElement;
        this.classList.add('is-open');
        document.body.classList.add('drawer-open');
        document.addEventListener('keydown', this.boundKeydown);
        var target = this.querySelector('[data-autofocus]') || this.panel;
        window.setTimeout(
          function () {
            if (target) {
              if (!target.hasAttribute('tabindex') && !target.matches(FOCUSABLE)) {
                target.setAttribute('tabindex', '-1');
              }
              target.focus({ preventScroll: true });
            }
          },
          reducedMotion.matches ? 0 : 150
        );
      }

      close() {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
        document.removeEventListener('keydown', this.boundKeydown);
        if (this.trigger && document.contains(this.trigger)) {
          this.trigger.focus();
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          trapFocus(this, event);
        }
      }
    };
  };

  /* Navigation drawer */
  class NavDrawer extends DrawerBase(HTMLElement) {}
  customElements.define('nav-drawer', NavDrawer);

  /* Details-less disclosure toggles (nav drawer submenus, filters) */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-disclosure-toggle]');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });

  /* Desktop dropdown menus */
  document.querySelectorAll('[data-submenu-toggle]').forEach(function (toggle) {
    var item = toggle.closest('.site-nav__item');
    var submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!item || !submenu) return;

    function openMenu() {
      toggle.setAttribute('aria-expanded', 'true');
      submenu.hidden = false;
    }
    function closeMenu() {
      toggle.setAttribute('aria-expanded', 'false');
      submenu.hidden = true;
    }

    toggle.addEventListener('click', function () {
      toggle.getAttribute('aria-expanded') === 'true' ? closeMenu() : openMenu();
    });
    item.addEventListener('mouseenter', openMenu);
    item.addEventListener('mouseleave', closeMenu);
    item.addEventListener('focusout', function (event) {
      if (!item.contains(event.relatedTarget)) closeMenu();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        closeMenu();
        toggle.focus();
      }
    });
  });

  /* ------------------------------------------------------------------ *
   * Quantity input                                                     *
   * ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach(
        function (button) {
          button.addEventListener('click', this.onButton.bind(this));
        }.bind(this)
      );
    }

    onButton(event) {
      event.preventDefault();
      var button = event.currentTarget;
      var previous = this.input.value;
      if (button.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }
      if (previous !== this.input.value) {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ *
   * Cart drawer + cart updates via Section Rendering API               *
   * ------------------------------------------------------------------ */
  class CartDrawer extends DrawerBase(HTMLElement) {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('change', this.onQuantityChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      var remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.updateLine(remove.dataset.index, 0);
        return;
      }
      var addon = event.target.closest('[data-addon-add]');
      if (addon) {
        event.preventDefault();
        addon.setAttribute('aria-disabled', 'true');
        window.MNRLCart.add({ id: addon.dataset.addonAdd, quantity: 1 }).finally(function () {
          addon.removeAttribute('aria-disabled');
        });
      }
    }

    onQuantityChange(event) {
      var input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      this.updateLine(input.dataset.index, parseInt(input.value, 10));
    }

    updateLine(line, quantity) {
      var body = this.querySelector('[data-cart-drawer-content]');
      if (body) body.style.opacity = '0.5';
      window.MNRLCart.change(line, quantity);
    }

    refresh(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var next = parsed.querySelector('[data-cart-drawer-content]');
      var current = this.querySelector('[data-cart-drawer-content]');
      if (next && current) {
        current.innerHTML = next.innerHTML;
        current.style.opacity = '';
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Cart API helper */
  window.MNRLCart = {
    sectionsForRequest: function () {
      var sections = ['cart-drawer-items'];
      return sections.join(',');
    },

    add: function (payload) {
      var item = { id: payload.id, quantity: payload.quantity || 1 };
      if (payload.properties) item.properties = payload.properties;
      if (payload.selling_plan) item.selling_plan = payload.selling_plan;
      return this.addMultiple([item]);
    },

    addMultiple: function (items) {
      var routes = window.MNRL.routes;
      var body = {
        items: items,
        sections: this.sectionsForRequest(),
        sections_url: window.location.pathname
      };

      return fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(
          function (data) {
            this.afterMutation(data);
            announce(window.MNRL.strings.added);
            return data;
          }.bind(this)
        )
        .catch(function (error) {
          var message =
            (error && error.description) || window.MNRL.strings.cartError;
          announce(message);
          throw error;
        });
    },

    change: function (line, quantity) {
      var routes = window.MNRL.routes;
      return fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: parseInt(line, 10),
          quantity: quantity,
          sections: this.sectionsForRequest(),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(this.afterMutation.bind(this))
        .catch(function (error) {
          var message =
            (error && error.description) || window.MNRL.strings.cartError;
          announce(message);
          var drawer = document.querySelector('cart-drawer');
          if (drawer) {
            var body = drawer.querySelector('[data-cart-drawer-content]');
            if (body) body.style.opacity = '';
          }
        });
    },

    afterMutation: function (data) {
      var cart = data.items !== undefined && data.token ? data : null;
      // /cart/add returns the added lines, not the cart; fetch counts from sections instead.
      this.updateCount(data);
      var drawer = document.querySelector('cart-drawer');
      if (drawer && data.sections && data.sections['cart-drawer-items']) {
        drawer.refresh(data.sections['cart-drawer-items']);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    updateCount: function (data) {
      var bubble = document.querySelector('[data-cart-count]');
      if (!bubble) return;
      if (typeof data.item_count === 'number') {
        this.setCount(bubble, data.item_count);
      } else {
        fetch(window.MNRL.routes.cart + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          })
          .then(
            function (cart) {
              this.setCount(bubble, cart.item_count);
            }.bind(this)
          );
      }
    },

    setCount: function (bubble, count) {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.setAttribute('data-count', String(count));
    },

    openDrawer: function (trigger) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.open(trigger);
    }
  };

  /* Header triggers for drawers */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    var drawer = document.querySelector(trigger.dataset.drawerOpen);
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ------------------------------------------------------------------ *
   * Product form (AJAX add to cart)                                    *
   * ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      if (window.MNRL.cartType !== 'drawer' || document.body.classList.contains('template-cart')) {
        return; // let native form submission take the shopper to the cart page
      }
      event.preventDefault();
      var formData = new FormData(this.form);
      var payload = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity') || '1', 10)
      };
      var sellingPlan = formData.get('selling_plan');
      if (sellingPlan) payload.selling_plan = sellingPlan;

      var properties = {};
      var hasProperties = false;
      formData.forEach(function (value, key) {
        var match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') {
          properties[match[1]] = value;
          hasProperties = true;
        }
      });
      if (hasProperties) payload.properties = properties;

      var button = this.submitButton;
      var label = button ? button.querySelector('[data-atc-text]') || button : null;
      var original = label ? label.textContent : '';
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = window.MNRL.strings.adding;
      }

      window.MNRLCart.add(payload)
        .then(function () {
          window.MNRLCart.openDrawer(button);
        })
        .catch(function () {
          /* announced in MNRLCart.add */
        })
        .finally(function () {
          if (button) {
            button.removeAttribute('aria-disabled');
            if (label) label.textContent = original;
          }
        });
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------ *
   * Accordion (FAQ, product details)                                   *
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-accordion-toggle]');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });

  /* ------------------------------------------------------------------ *
   * Search panel + predictive search                                   *
   * ------------------------------------------------------------------ */
  class SearchPanel extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.boundKeydown = this.onKeydown.bind(this);
      this.debounce = null;

      this.querySelectorAll('[data-search-close]').forEach(
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      if (this.input && this.results) {
        this.input.addEventListener(
          'input',
          function () {
            window.clearTimeout(this.debounce);
            this.debounce = window.setTimeout(this.fetchResults.bind(this), 300);
          }.bind(this)
        );
      }
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      document.addEventListener('keydown', this.boundKeydown);
      window.setTimeout(
        function () {
          if (this.input) this.input.focus();
        }.bind(this),
        reducedMotion.matches ? 0 : 150
      );
    }

    close() {
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.boundKeydown);
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        trapFocus(this, event);
      }
    }

    fetchResults() {
      var query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      var url =
        window.MNRL.routes.predictiveSearch +
        '?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,page,article&resources[limit]=4&section_id=predictive-search';
      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var section = parsed.querySelector('#shopify-section-predictive-search');
            this.results.innerHTML = section ? section.innerHTML : '';
          }.bind(this)
        )
        .catch(function () {
          /* predictive search is an enhancement — the form still submits */
        });
    }
  }
  customElements.define('search-panel', SearchPanel);

  /* ------------------------------------------------------------------ *
   * Modal (image zoom)                                                 *
   * ------------------------------------------------------------------ */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      this.boundKeydown = this.onKeydown.bind(this);
      this.querySelectorAll('[data-modal-close]').forEach(
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );
      var overlay = this.querySelector('.modal__overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      document.addEventListener('keydown', this.boundKeydown);
      var close = this.querySelector('[data-modal-close]');
      if (close) close.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        trapFocus(this, event);
      }
    }
  }
  customElements.define('modal-dialog', ModalDialog);

  /* ------------------------------------------------------------------ *
   * Quick add (product cards)                                          *
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;
    button.setAttribute('aria-disabled', 'true');
    var original = button.textContent;
    button.textContent = window.MNRL.strings.adding;
    window.MNRLCart.add({ id: button.dataset.quickAdd, quantity: 1 })
      .then(function () {
        window.MNRLCart.openDrawer(button);
      })
      .catch(function () {})
      .finally(function () {
        button.removeAttribute('aria-disabled');
        button.textContent = original;
      });
  });

  /* ------------------------------------------------------------------ *
   * Scroll reveal (respects reduced motion)                            *
   * ------------------------------------------------------------------ */
  function initReveal() {
    var targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    targets.forEach(function (el) {
      observer.observe(el);
    });
  }
  initReveal();
  document.addEventListener('shopify:section:load', initReveal);

  /* ------------------------------------------------------------------ *
   * Header: add blurred background once page is scrolled               *
   * ------------------------------------------------------------------ */
  (function () {
    var header = document.querySelector('.site-header-wrapper');
    if (!header) return;
    var ticking = false;

    function updateHeader() {
      header.classList.toggle('header--scrolled', window.scrollY > 80);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });

    updateHeader();
  })();

  /* ------------------------------------------------------------------ *
   * Recently viewed (localStorage, product handles)                    *
   * ------------------------------------------------------------------ */
  window.MNRLRecentlyViewed = {
    key: 'mnrl:recently-viewed',
    get: function () {
      try {
        return JSON.parse(window.localStorage.getItem(this.key)) || [];
      } catch (error) {
        return [];
      }
    },
    push: function (handle) {
      var handles = this.get().filter(function (h) {
        return h !== handle;
      });
      handles.unshift(handle);
      try {
        window.localStorage.setItem(this.key, JSON.stringify(handles.slice(0, 8)));
      } catch (error) {
        /* private mode — skip silently */
      }
    }
  };

  document.querySelectorAll('[data-recently-viewed]').forEach(function (container) {
    var exclude = container.dataset.exclude || '';
    var handles = window.MNRLRecentlyViewed.get()
      .filter(function (h) {
        return h !== exclude;
      })
      .slice(0, parseInt(container.dataset.limit || '4', 10));
    if (!handles.length) return;

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '?view=card')
          .then(function (r) {
            return r.ok ? r.text() : '';
          })
          .catch(function () {
            return '';
          });
      })
    ).then(function (cards) {
      var html = cards.join('');
      if (!html.trim()) return;
      container.querySelector('[data-recently-viewed-grid]').innerHTML = html;
      var section = container.closest('.shopify-section') || container;
      section.classList.remove('hidden');
      container.classList.remove('hidden');
    });
  });
})();
